import { getAttributesMap } from './attributes-map'
import { getMetadata } from '../utils/metadata'
import type { LandingPage, LandingPageAttributes, LandingPageGroupList } from './types'

/**
 * Extracts unique group names from the landing page data.
 *
 * @param {string} pageId - Landing page id.
 * @param {string[]} listIds - Recommendation lists ids.
 * @returns An array of unique group names derived from the `metadata`.
 */
function getGroupsNames(pageId: string, listIds: string[]) {
  const groups: string[] = []

  if (!pageId && !listIds) {
    return
  }

  listIds.forEach(listId => {
    const { productsListType } = getMetadata(pageId, listId) ?? {}

    if (!productsListType || groups.includes(productsListType)) {
      return
    }

    groups.push(productsListType)
  })

  return groups
}

/**
 * Retrieves attributes for a specific page and product list based on the provided metadata and attributes map.
 *
 * @param {string} pageId - The unique identifier for the page.
 * @param {string} listId - The unique identifier for the products list.
 * @returns {LandingPageAttributes} An object containing the attributes for the page and product list, or an empty object if not found.
 *
 * @example
 * const attributesMap = new Map()
 * attributesMap.set('product', {
 *   b2c: {
 *     personalized: {
 *       popular: {
 *         default: { ctaText: 'Shop Now', colorTheme: 'gray' },
 *         laptops: {
 *           ctaText: 'View All'
 *         }
 *       }
 *     }
 *   }
 * })
 * const attributes = getAttributes('product-b2c-personalized', 'popular-laptops', attributesMap)
 * console.log(attributes)
 * // Output:
 * // {
 * //   ctaText: 'View all',
 * //   colorTheme: 'gray'
 * // }
 *
 * @throws Will log errors to the console if the `pageId` or `listId` metadata is invalid, or if attributes cannot be retrieved.
 */
function getAttributes(pageId: string, listId: string): LandingPageAttributes {
  const {
    pageType,
    pageBusinessType,
    pagePersonalizationType,
    productsListType,
    productsListCategory,
  } = getMetadata(pageId, listId) ?? {}

  const attributesMap = getAttributesMap()

  if (!pageType || !productsListType) {
    console.error(`Data not found in list metadata`)
    return {}
  }

  const cleanPageId = pageId.replace('/', '')
  const attributes = attributesMap.get(pageType) || attributesMap.get(cleanPageId)

  if (!attributes) {
    console.error(`Page type "${pageType}" not found in attributes map`)
    return {}
  }

  try {
    if (!attributesMap.has(cleanPageId) && pageBusinessType && pagePersonalizationType) {
      const selectedAttributes =
        attributes?.[pageBusinessType]?.[pagePersonalizationType]?.[productsListType]

      return {
        ...selectedAttributes?.default,
        ...(selectedAttributes?.[productsListCategory || ''] || {}),
      }
    }

    return {
      ...attributes?.[productsListType]?.default,
      ...(attributes?.[productsListType]?.[productsListCategory || ''] || {}),
    }
  } catch (error) {
    console.error('Failed to retrieve attributes', error)
    return {}
  }
}
/**
 * Processes a `LandingPage` object to extract recommendation groups and their associated product lists.
 *
 * This service function takes a `LandingPage`, checks if both `pageId` and `recommendationList` are defined,
 * and then organizes the recommendation lists into named groups. Each group is determined by matching
 * recommendation list IDs to specific group name patterns obtained via `getGroupsNames`.
 *
 * Within each group, it filters out recommendation lists that:
 * - Contain the group name in their ID (case-insensitive).
 * - Have at least one product reference.
 *
 * For each valid recommendation list, it builds a grouped entry that includes:
 * - The attributes resolved via `getAttributes`.
 * - The non-empty list of product references.
 *
 * @param landingPage - The `LandingPage` object containing page information and recommendation lists.
 *
 * @returns An object with:
 * - `groupsNames`: An array of group name strings derived from the page and list IDs.
 * - `groups`: A `Map` where each group name maps to an array of objects containing attributes and product references.
 *
 * Returns `undefined` if either `pageId` or `recommendationList` is missing.
 *
 */
export const landingPageService = (landingPage: LandingPage) => {
  const { pageId, recommendationList } = landingPage ?? {}

  if (!pageId || !recommendationList) {
    return
  }

  const groups = new Map<string, LandingPageGroupList[]>()

  const listsIds = recommendationList?.map(list => list?.id as string) ?? []
  const groupsNames = getGroupsNames(pageId, listsIds) ?? []
  groupsNames.forEach(groupName => {
    const groupLists = recommendationList
      .filter(
        list =>
          list?.id?.toLocaleLowerCase().includes(groupName) && list?.productReferences!.length > 0
      )
      .map(list => ({
        attributes: getAttributes(pageId, list?.id || ''),
        productReferences: list?.productReferences || [],
      })) as LandingPageGroupList[]

    groups.set(groupName, groupLists)
  })

  return {
    groupsNames,
    groups,
  }
}
